import React, { useState, useEffect, useRef } from "react";
import { FaSort, FaChevronDown } from "react-icons/fa";

const InventorySortFilter = ({ inventoryItems, onSortChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentSort, setCurrentSort] = useState({ field: "last_updated_on", direction: "desc" });
  const containerRef = useRef(null);

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, { passive: true });
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const sortOptions = [
    { label: "Last Updated (Newest First)", field: "last_updated_on", direction: "desc" },
    { label: "Last Updated (Oldest First)", field: "last_updated_on", direction: "asc" },
    { label: "Material Name (A-Z)", field: "material_name", direction: "asc" },
    { label: "Material Name (Z-A)", field: "material_name", direction: "desc" },
    { label: "Current Stock (High to Low)", field: "current_stock", direction: "desc" },
    { label: "Current Stock (Low to High)", field: "current_stock", direction: "asc" },
    { label: "Added Stock (High to Low)", field: "added_stock", direction: "desc" },
    { label: "Added Stock (Low to High)", field: "added_stock", direction: "asc" },
    { label: "Used Stock (High to Low)", field: "used_stock", direction: "desc" },
    { label: "Used Stock (Low to High)", field: "used_stock", direction: "asc" },
    { label: "Subcategory (A-Z)", field: "subcategory", direction: "asc" },
    { label: "Subcategory (Z-A)", field: "subcategory", direction: "desc" },
  ];

  const handleSort = (sortOption) => {
    setCurrentSort(sortOption);
    setIsOpen(false);

    // Create a sorted copy of inventory items based on the selected sort option
    const sortedItems = [...inventoryItems].sort((a, b) => {
      // Special handling for dates
      if (sortOption.field === "last_updated_on" || sortOption.field === "created_on") {
        const dateA = new Date(a[sortOption.field]);
        const dateB = new Date(b[sortOption.field]);
        return sortOption.direction === "asc" 
          ? dateA - dateB 
          : dateB - dateA;
      }
      
      // Handling for text fields (alphabetical sorting)
      if (sortOption.field === "material_name" || sortOption.field === "subcategory") {
        const valueA = (a[sortOption.field] || "").toLowerCase();
        const valueB = (b[sortOption.field] || "").toLowerCase();
        
        if (sortOption.direction === "asc") {
          return valueA.localeCompare(valueB);
        } else {
          return valueB.localeCompare(valueA);
        }
      }
      
      // Default handling for numeric fields
      const valueA = a[sortOption.field] || 0;
      const valueB = b[sortOption.field] || 0;
      
      if (sortOption.direction === "asc") {
        return valueA - valueB;
      } else {
        return valueB - valueA;
      }
    });

    // Call the parent component's callback with the sorted results
    onSortChange(sortedItems);
  };

  return (
    <div className="relative inline-block" ref={containerRef}>
      <button
        className="h-7 bg-gray-100 text-gray-700 text-sm font-medium px-4 rounded-md flex items-center space-x-2 hover:bg-gray-200"
        onClick={() => setIsOpen(!isOpen)}
      >
        <FaSort className="text-gray-600" />
        <span>Sort</span>
        <FaChevronDown className={`text-gray-600 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg z-10 py-1 border border-gray-200">
          {sortOptions.map((option, index) => (
            <button
              key={index}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                currentSort.field === option.field && currentSort.direction === option.direction
                  ? "bg-purple-50 text-purple-700 font-medium"
                  : "text-gray-700"
              }`}
              onClick={() => handleSort(option)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default InventorySortFilter;